import { useCallback } from "react";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { savePromo, createSmartPromo } from "../../../api";
import { stripComboPropertyIds } from "../utils/roomRateCombos";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { EMPTY_COMBO_WARNING_MESSAGE } from "../../../constants/promoValidation";
import { logEmptyComboBlocked } from "../../../utils/promoComboValidation";
import {
  getSaveType,
  getActionType,
  getFailedNames,
  getErrorMessages,
  resolveSmartPromoType,
  buildSmartPromoSuccessMessage,
} from "../utils/promoSubmitMessages";

/**
 * Non-pilot submit branch — savePromo / createSmartPromo path.
 * Handles market context, action-center smart promos, multi-property,
 * and standard create/edit/duplicate flows.
 */
export function useSubmitStandardPromo({
  promo,
  selectedProperty,
  properties,
  isEdit,
  isDuplicate,
  isMarket,
  calculationType,
  hideModal,
  setLoading,
  // Shared with the derived branch via useCreatePromoSubmit: resolves the
  // combos that belong to one property (EO-1060 cross-property guard).
  getCodesForProperty,
  recordRecommendationUsed,
}) {
  const buildPromoPayloadBase = useCallback(
    (token, propertyCode, allowedRateRoomCodes, promoType) => {
      const eventType =
        isEdit && !isMarket && !isDuplicate
          ? "Promo Modified"
          : "Promo Created";
      return {
        ...promo,
        propertyCode: propertyCode || selectedProperty?.propertyCode || "",
        // The legacy promo API only knows the four original combo fields.
        allowedRateRoomCodes: stripComboPropertyIds(allowedRateRoomCodes),
        eventType,
        username: jwtDecode(token)["custom:username"],
        promoType: promoType,
        selectedProperties: undefined,
        allowedRateRoomCodesByProperty: undefined,
        roomsRateplansMode: undefined,
        roomRatePlansLoading: undefined,
      };
    },
    [promo, selectedProperty, isMarket, isDuplicate, isEdit]
  );

  const handleAllSuccess = useCallback(
    (responseData, isDraft) => {
      const total = responseData?.total || 0;
      const successful = responseData?.successful || 0;
      const plural = successful > 1 ? "s" : "";
      hideModal();
      const message =
        total === 1
          ? "Promo " +
            getSaveType({
              isMarket,
              isEditMode: isEdit,
              isDuplicateMode: isDuplicate,
              isDraft,
            }) +
            " successfully"
          : successful +
            " promo" +
            plural +
            " " +
            getActionType({ isMarket, isEdit, isDuplicate }) +
            " successfully";
      toast.success(message);
    },
    [hideModal, isEdit, isDuplicate, isMarket]
  );

  const handlePartialSuccess = useCallback(
    (results, successful, failed) => {
      const plural = successful > 1 ? "s" : "";
      const failedNames = getFailedNames(results);
      const failedSuffix = failedNames ? ": " + failedNames : "";
      toast.warning(
        successful +
          " promo" +
          plural +
          " " +
          getActionType({ isMarket, isEdit, isDuplicate }) +
          " successfully, but " +
          failed +
          " failed" +
          failedSuffix
      );
    },
    [isMarket, isEdit, isDuplicate]
  );

  const handleAllFailed = useCallback(
    (results, total) => {
      const saveVerb = isDuplicate ? "duplicate" : "save";
      const errors = getErrorMessages(results);
      const plural = total > 1 ? "s" : "";
      const errorSuffix = errors ? ". " + errors : "";
      toast.error("Failed to " + saveVerb + " promo" + plural + errorSuffix);
    },
    [isDuplicate]
  );

  const handleSaveSuccess = useCallback(
    (responseData, isDraft) => {
      const results = responseData?.results || [];
      const total = responseData?.total || 0;
      const successful = responseData?.successful || 0;
      const failed = responseData?.failed || 0;

      if (failed === 0 && successful > 0) {
        handleAllSuccess(responseData, isDraft);
      } else if (successful > 0 && failed > 0) {
        handlePartialSuccess(results, successful, failed);
      } else {
        handleAllFailed(results, total);
      }
    },
    [handleAllSuccess, handlePartialSuccess, handleAllFailed]
  );

  const submitStandardPromo = useCallback(
    (isDraft = false) => {
      setLoading(true);
      const token = localStorage.getItem("ROCP_idToken");

      const selectedProperties = promo?.selectedProperties || [];
      const allowedRateRoomCodesByProperty =
        promo?.allowedRateRoomCodesByProperty;
      const hasMultipleProperties =
        selectedProperties.length > 1 && allowedRateRoomCodesByProperty;

      const promoType = resolveSmartPromoType(promo, isMarket);

      let promoPayload;

      if (isMarket || !hasMultipleProperties) {
        // This payload is always sent for the property in the header
        // (promo.propertyCode is kept in sync with it), so the selection has
        // to belong to that property.
        const allowedRateRoomCodes = getCodesForProperty(
          promo?.allowedRateRoomCodes,
          selectedProperty?.propertyId
        );
        promoPayload = buildPromoPayloadBase(
          token,
          promo?.propertyCode,
          allowedRateRoomCodes,
          promoType
        );
      } else {
        promoPayload = selectedProperties.map((propertyId) => {
          const property = properties?.find((p) => p.propertyId === propertyId);
          const propertyCode = property?.propertyCode || propertyId;
          const propertyRoomRateCodes = getCodesForProperty(
            allowedRateRoomCodesByProperty[propertyId] ||
              promo?.allowedRateRoomCodes,
            propertyId
          );
          return buildPromoPayloadBase(
            token,
            propertyCode,
            propertyRoomRateCodes,
            promoType
          );
        });
      }

      // The Launch button only knows whether *something* is selected, not
      // whether it survives the per-property filter — a selection made against
      // another property is dropped here and would submit an empty payload.
      // Drafts are allowed to be incomplete, so they skip this.
      if (!isDraft) {
        const payloadsToCheck = Array.isArray(promoPayload)
          ? promoPayload
          : [promoPayload];
        const isMissingCodes = payloadsToCheck.some(
          (payload) => !payload?.allowedRateRoomCodes?.length
        );
        if (isMissingCodes) {
          setLoading(false);
          toast.warning(EMPTY_COMBO_WARNING_MESSAGE);
          logEmptyComboBlocked({
            action: isEdit && !isDuplicate ? "update" : "create",
            promoId: promo?.id,
            propertyId: selectedProperty?.propertyId,
          });
          return;
        }
      }

      // Use createSmartPromo for action center smart promos, savePromo for everything else
      const { suggestionPromoId, promoCategory, ...cleanPayload } =
        Array.isArray(promoPayload)
          ? { ...promoPayload[0] }
          : { ...promoPayload };
      const isSmartPromo = !!(suggestionPromoId && promoCategory);
      const saveCall = isSmartPromo
        ? createSmartPromo(cleanPayload, calculationType, {
            suggestionPromoId,
            promoCategory,
          })
        : savePromo(promoPayload, isDraft, calculationType);

      return saveCall
        .then(async (res) => {
          if (res?.data?.success) {
            // savePromo is a batch: success:true only means the request was
            // accepted, and every per-property item can still have failed —
            // handleSaveSuccess routes that to handleAllFailed. `successful` is the
            // real count, and recording the card off the outer flag alone would
            // dismiss it with no promo to show for it. createSmartPromo is
            // single-promo, so success:true is conclusive there.
            const promoWasCreated =
              isSmartPromo || (res.data.data?.successful ?? 0) > 0;
            if (promoWasCreated) {
              await recordRecommendationUsed(suggestionPromoId, promoCategory);
            }
            if (isSmartPromo) {
              // Action center smart promo — show category-specific toast and close drawer
              toast.success(
                buildSmartPromoSuccessMessage(
                  promoCategory,
                  "Promotion",
                  "created"
                )
              );
              hideModal();
            } else {
              handleSaveSuccess(res.data.data, isDraft);
            }
          } else {
            const errorMessage = getApiErrorMessage(res?.data);
            toast.error(
              `Failed to ${isDuplicate ? "duplicate" : "save"} promo, error: ${errorMessage}`
            );
          }

          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          toast.error(`Failed to save promo: ${getApiErrorMessage(err)}`);
        });
    },
    [
      setLoading,
      promo,
      isEdit,
      isMarket,
      isDuplicate,
      properties,
      selectedProperty,
      getCodesForProperty,
      buildPromoPayloadBase,
      calculationType,
      hideModal,
      handleSaveSuccess,
      recordRecommendationUsed,
    ]
  );

  return { submitStandardPromo };
}
